import { clearFilterParams } from '@btime/react-contexts'
import { gql } from '@apollo/client'
import { pathOr } from 'ramda'
import { useSessionPermission, useCustomToken } from '@btime/react-session'
import React from 'react'

import { placesClient } from '../../../utils/apollo/api'
import { useOpenEditPlaceModal } from '../../../utils/routes/modals/modalsActions'
import { usePromiseQuery } from '../../../utils/apollo/_helpers/promiseQuery'
import Select from '../../../packages/kiti/components/form/Select'
import SelectWithInterpolation from '../../../packages/kiti-editor/components/select/SelectWithInterpolation'
import { cleanWhenEnabledFilterIsSet } from '../../../modules/_helpers/filterByEnabled'
import { mountFilterByFieldValue } from './_helper/filterByFieldValue'

const QUERY = gql`
  query Places($search: String, $page: Int, $limit: Int, $filter: PlaceFilter, $formToken: String) {
    places(
      search: $search
      page: $page
      limit: $limit
      filter: $filter
      formToken: $formToken
      sort: { field: NAME, type: ASC }
    ) {
      id
      name
      resume
      type
      locks {
        id
        reason
      }
      fieldValues {
        id
        value
        field {
          id
          name
          type
        }
        __typename
      }
    }
  }
`

export const usePlacesLoader = filter => {
  filter = cleanWhenEnabledFilterIsSet({ ...filter })
  const formToken = useCustomToken('formToken')
  const query = usePromiseQuery(QUERY, { client: placesClient })
  return (search, page = 1, limit = 15) => {
    return query({ search, page, formToken, limit, filter: clearFilterParams(filter || {}) })?.then(
      pathOr([], ['data', 'places'])
    )
  }
}

const PlaceSelect = ({
  onChange,
  onOptionsChange,
  value,
  type,
  filter,
  filterByFieldValue,
  disableItem,
  reasonForDisablePresenter,
  interpolations = [],
  ...props
}) => {
  const canCreateNew = useSessionPermission('createLocation')
  const fieldValue = mountFilterByFieldValue('place', filterByFieldValue)
  filter = { ...filter, fieldValue }
  const getOptions = usePlacesLoader({ types: type, ...(filter || {}) })
  const openCreateModal = useOpenEditPlaceModal({ outside: true })

  const [options, setOptions] = React.useState([])

  const loadOptions = React.useCallback(
    async (search, page) => {
      const result = await getOptions(search, page)
      setOptions(result)
      return result
    },
    [getOptions]
  )

  React.useEffect(() => {
    if (onOptionsChange) {
      onOptionsChange(options)
    }
  }, [options, onOptionsChange])

  const shouldDisable = place => {
    return !!reasonForDisablePresenter && !!place?.locks?.length
  }

  const Component = !!interpolations.length ? SelectWithInterpolation : Select

  return (
    <Component
      value={value} 
      onChange={onChange}
      onCreate={
        canCreateNew &&
        (name => {
          openCreateModal({ onSave: onChange, initialState: { name, type } })
        })
      }
      options={loadOptions}
      descriptionField="resume"
      disableItem={shouldDisable}
      reasonForDisablePresenter={reasonForDisablePresenter}
      interpolations={interpolations}
      origin='place'
      {...props}
    />
  )
}

export default PlaceSelect